import type {Ref} from 'react'
import type {ConversationComment} from '../../page-data/payloads/conversation-comments'
import {ConversationCommentBox, type ConversationCommentBoxHandle} from '@github-ui/diff-lines/conversation-comment-box'
import type {UseMutationResult} from '@github-ui/react-query'
import {Banner, Stack} from '@primer/react'
import {clsx} from 'clsx'

import styles from './ConversationItemBody.module.css'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import {
  applyVariablesToReactions,
  type ReactToIssueCommentResponse,
  type ReactToIssueCommentVariables,
} from '../../mutations/use-react-to-issue-comment-mutation'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import type {ConversationItemBodyEditManager} from './use-body-edit-manager'
import {itemTypeName} from './utils/item-type-name'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {StaleContentError} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

type ReactToItemMutation = UseMutationResult<ReactToIssueCommentResponse, Error, ReactToIssueCommentVariables, unknown>

interface ConversationItemBodyProps {
  item: ConversationComment
  editorRef: Ref<ConversationCommentBoxHandle>
  editorContainerRef: Ref<HTMLDivElement>
  editManager: ConversationItemBodyEditManager
  reactMutation: ReactToItemMutation
}

/** Editable item body and reactions for conversation items. */
export function ConversationItemBody({
  item,
  editorRef,
  editorContainerRef,
  editManager,
  reactMutation,
}: ConversationItemBodyProps) {
  const optimisticReactionGroups = reactMutation?.isPending
    ? applyVariablesToReactions(item.reactionGroups, reactMutation.variables)
    : item.reactionGroups

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) =>
    reactMutation?.mutate({
      commentId: String(item.id),
      reaction,
      viewerHasReacted,
    })

  const typeName = itemTypeName(item)

  const errorBanner = editManager.error && (
    <Banner
      variant="critical"
      role="alert"
      onDismiss={editManager.dismissError}
      title={`Failed to update ${typeName.lowercase}`}
    >
      {editManager.error.message}
    </Banner>
  )
  const contentIsStale = editManager.error instanceof StaleContentError

  return editManager.editingBody !== null ? (
    <div ref={editorContainerRef}>
      <Stack direction="vertical" gap="condensed">
        {!contentIsStale && errorBanner}
        <ConversationCommentBox
          label={`Edit ${typeName.lowercase}`}
          value={editManager.editingBody}
          onChange={editManager.handleDraftChange}
          onSave={editManager.submit}
          validationResult={editManager.validationResult}
          onCancel={editManager.cancel}
          saveButtonText={`Update ${typeName.lowercase}`}
          maxHeightLines={15}
          ref={editorRef}
          contentIsStale={contentIsStale}
          saveButtonTrailingIcon={false}
        />
      </Stack>
    </div>
  ) : (
    <Stack direction="vertical" gap="condensed">
      {errorBanner}
      <NewMarkdownViewer
        className={clsx(editManager.isPending && styles.pendingComment)}
        verifiedHTML={editManager.optimisticBodyHtml}
        disabled={editManager.isPending}
        markdownValue={editManager.optimisticBody}
        onChange={editManager.handleDirectMarkdownChange}
      />
      {item.reactionGroups.length > 0 && (
        <ReactionViewerBase
          reactionGroups={optimisticReactionGroups}
          canReact={item.viewerCanReact}
          onReact={onReact}
        />
      )}
    </Stack>
  )
}
